// Confianza gate (spec invariant 10, ADR-0129 §5, ADR-0119, ADR-0121) —
// the ONE home of the "does this observation still need a human before
// the acta can be emitted" predicate and of the deterministic "what is
// missing" code the PWA and the dashboard render.
//
// FIVE places must stay consistent with the answer this file gives.
// This header used to say three and undercounted itself; ADR-0121
// enumerated the real set, so the next reader does not inherit the same
// blind spot:
//   1. the 409 CONFIANZA_REVIEW_REQUIRED of POST /reports/:id/sign
//      (via lib/confianza-gate.ts `loadConfianzaPendientes`);
//   2. GET /reports/:id/confianza-pendientes — same builder;
//   3. the `falta` field of the GET /findings/:id read model (PR-3),
//      which calls this predicate directly;
//   4. the `lowConfidencePending` counter of GET
//      /postventa-dashboard/taxonomy-metrics (routes/postventa-dashboard.ts,
//      the D2 queue badge) — SQL that never reaches the drift-throw, so
//      a divergence there is silent at runtime;
//   5. the `confianza_gate_raised_at` marker written by
//      lib/taxonomy-intake.ts. It does not decide retention, and since
//      ADR-0121 it does not track it either: the marker is stamped only
//      when a run lands the row under the confidence threshold, so a row
//      retained solely by a missing axis is retained and never marked.
//      That undercount is deliberate — ratified 2026-09-04. Widening it
//      would spend the marker's monotonic meaning, which is the premise
//      of the cheap turn to a sticky gate. Anything that needs "the rows
//      the gate is holding" builds on `confianzaPendienteWhere`, never
//      on that column; it has no reader today.
//
// (1), (2) and (4) run the SQL twin of `revisionPendiente`, built once
// in core-api (lib/confianza-gate.ts, `confianzaPendienteWhere`) and
// served by findings_org_confianza_pending_idx (migration 0056). That
// partial index (confianza_review_at IS NULL AND taxonomy_version IS
// NOT NULL) stays a strict superset of the predicate, which is why the
// taxonomy axes could join it with no migration — same seven columns
// (F5 adds intake_channel) on both sides, same NULL-fails-closed
// posture (fitness: confianza-gate-single-predicate).
//
// Deliberately NOT here: copy. `faltaCode` returns a code; the label
// is rendered by core-api (lib/confianza-gate.ts FALTA_LABEL) so the
// engine stays free of user-facing strings.
//
// `confianza_gate_raised_at` is NOT read by any function in this file
// on purpose — council 2026-09-01 (option C): it is an audit marker
// only. A future sticky gate is a predicate change here plus a
// superseding ADR, never a migration.
import { isOwnerAuthoredIntake } from "./intake-channel.js";
import { CONFIANZA_IA_UMBRAL_REVISION } from "./taxonomy.js";
/**
 * What the supervisor still has to supply for the gate to lift. Wire
 * enum (packages/api-types derives `confianzaFaltaSchema` from it).
 *
 * `evidencia_contexto` is reserved for the capture flow (P4 asks for a
 * wide shot before the detail shot) — `faltaCode` never emits it today:
 * a context photo is never what blocks emission, the detail photo is.
 *
 * `tipo_reparacion` is RESERVADO on the same terms: `faltaCode()` never
 * emits it and `revisionPendiente` never looks at it. Adding a fourth
 * axis to the gate would change the retention of every classified row
 * (ADR-0121), which is a predicate decision, not a vocabulary one. Its
 * only emitter today is the `faltan[]` of
 * `POST /assets/:id/observations/classify`.
 */
export const CONFIANZA_FALTA_CODES = [
    "evidencia_contexto",
    "evidencia_detalle",
    "recinto",
    "elemento",
    "tipo_falla",
    "tipo_reparacion",
    "candidatos",
    "descripcion_libre",
];
/**
 * Los tres ejes ESTRUCTURALES de la taxonomía — el subconjunto de
 * `CONFIANZA_FALTA_CODES` que `revisionPendiente` exige presente
 * (ADR-0121) y que el 422 `CLASSIFICATION_AXES_REQUIRED` enumera en
 * `details.ejes`.
 *
 * Vivía en packages/api-types como copia a mano, guardada sólo por un
 * `satisfies ReadonlyArray<ConfianzaFaltaCode>` (#0646). Ese `satisfies`
 * verifica PERTENENCIA, jamás COBERTURA: un cuarto eje estructural
 * agregado acá dejaba la lista del wire atrás en silencio y en verde,
 * que es exactamente la deriva que #0006 y #0474 ya costaron dos veces.
 * Vive acá porque el predicado que lo define vive acá; api-types lo
 * importa y lo re-exporta para que el dashboard no tome una arista
 * contra core-engine.
 *
 * El orden se preserva tal como estaba en el wire: es el orden de
 * lectura del eje («¿dónde? ¿en qué? ¿qué le pasa?»), NO la prioridad
 * de remedio de `faltaCode` (que va elemento antes que recinto). Nadie
 * debe derivar prioridad de este array — para eso está `faltaCode`.
 */
export const CLASSIFICATION_AXIS_CODES = [
    "recinto",
    "elemento",
    "tipo_falla",
];
// Single source of truth for the capture-role domain (invariant 7 drift
// fix, 2026-09-02): wire schemas and hand-rolled unions across
// packages/api-types, core-api and web-svelte used to redeclare
// "contexto" | "detalle" independently — silent drift if a third role
// is ever added here. Everyone else derives from this array.
export const EVIDENCE_CAPTURE_ROLES = ["contexto", "detalle"];
/**
 * Handoff rule 1: a CLASSIFIED row (taxonomy_version stamped) with no
 * human review stamp still needs a human when either of TWO distinct
 * failures holds (ADR-0121). They are not two spellings of one check:
 *
 *   - the three structural axes (`roomCode`, `elementCode`,
 *     `failureTypeCode`) cover *"the model declared it does not know"*.
 *     A NULL axis is absent structural information, not a measurement
 *     of certainty — `SanitizedTaxonomyProposal` lets the classifier
 *     return `null` on any of the three, and it does.
 *   - the confidence threshold covers *"the model filled everything in
 *     but is not sure"* — every axis present, and the model itself
 *     reports doubt about the classification as a whole.
 *
 * Until 2026-09-04 only the second guard existed, and the first failure
 * mode walked straight through it: the reported case was `room_code`
 * NULL at `ai_confidence` 0.93 — the model knew *what* it was looking
 * at and did not know *where*, and a high number on one axis hid a
 * "no sé" on another. 4 of 4 AI-classified rows in the PR-54
 * environment had `room_code` NULL and 0 of 4 were retained.
 *
 * Order is load-bearing, not cosmetic. The presence clauses sit BELOW
 * the `confianzaReviewAt` early return because the human stamp lifts
 * the gate whatever the row looks like, and because the SQL twin
 * pre-filters on `confianza_review_at IS NULL` before this predicate
 * ever sees a row — hoisting them above the early return would be a
 * no-op here and would cost an index migration there, against this
 * module's contract of "predicate change, never a migration".
 *
 * The `<` on the threshold stays strict, reaffirmed as part of this
 * change (ADR-0121 §Decisión 2): three axes present + 0.74 retains,
 * three axes present + 0.75 does NOT.
 *
 * `evidenceRoles` takes no part in this predicate and still does not.
 * The detail photo is a *remedy* priority inside `faltaCode`, never a
 * *retention* criterion — the evidence axis is already protected
 * upstream by the CONFIANZA_SIN_EVIDENCIA_MAX clamp (taxonomy.ts).
 *
 * Rows without taxonomy_version (legacy, or verticals without the
 * primitive) never participate. OTR flags but does not block on its
 * own (founder 2026-09-01).
 *
 * F5 adds a second "this row does not participate" guard, and its
 * position is as load-bearing as the rest of the order: the
 * `intakeChannel === "reporte_propietario"` check sits directly BELOW
 * `taxonomyVersion === null` and ABOVE `confianzaReviewAt`, because it
 * is the same family as the former — a statement about whether the row
 * is in scope for the gate at all — and not a lift conditioned on a
 * one-off human act like the review stamp. The exemption is BY INTAKE
 * CHANNEL, never by role and never by recipe_slug: an observation born
 * in the owner's own report (`reporte_propietario`) is not the
 * supervisor's classification to hold, whoever is looking at it. Any
 * other value, NULL included, keeps participating — NULL is the canal
 * of every row that predates the column and must fail closed like the
 * rest of this predicate.
 */
export function revisionPendiente(row) {
    if (row.taxonomyVersion === null)
        return false;
    // ADR-0163 — también la consulta convertida: la contó el propietario y
    // la clasificó una persona del equipo (los cinco ejes, a mano).
    if (isOwnerAuthoredIntake(row.intakeChannel))
        return false;
    if (row.confianzaReviewAt !== null)
        return false;
    if (row.roomCode === null || row.elementCode === null || row.failureTypeCode === null)
        return true;
    return row.aiConfidence === null || row.aiConfidence < CONFIANZA_IA_UMBRAL_REVISION;
}
/**
 * Detail-photo state derived from the bound evidence roles:
 *   - no evidence at all → "missing" (there is certainly no detail shot);
 *   - some rows, every role NULL → "unknown" (pre-0054 data, or a PWA
 *     that never labelled) — the gate does NOT demand a photo it can't
 *     tell is absent; it falls through to the taxonomy checks;
 *   - any row labelled 'detalle' → "present";
 *   - roles known, none 'detalle' → "missing".
 */
function detailEvidenceState(roles) {
    if (roles.length === 0)
        return "missing";
    if (roles.some((r) => r === "detalle"))
        return "present";
    if (roles.every((r) => r === null))
        return "unknown";
    return "missing";
}
/**
 * Deterministic priority — the FIRST thing the supervisor should fix,
 * never a list: evidencia_detalle > elemento > recinto > tipo_falla >
 * candidatos > descripcion_libre. The last two split on how many
 * candidates the last run offered: two or more → pick one (P9); fewer
 * → describe it in your own words (P10, OTR + free text).
 *
 * Meaningful only when `revisionPendiente(row)` is true; callers that
 * compute it for a row that isn't pending get `descripcion_libre` at
 * worst, never a throw.
 */
export function faltaCode(row) {
    if (detailEvidenceState(row.evidenceRoles) === "missing")
        return "evidencia_detalle";
    if (row.elementCode === null)
        return "elemento";
    if (row.roomCode === null)
        return "recinto";
    if (row.failureTypeCode === null)
        return "tipo_falla";
    if (row.candidatosCount >= 2)
        return "candidatos";
    return "descripcion_libre";
}
//# sourceMappingURL=confianza.js.map